import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import {
  Store, Phone, Mail, Check, Search, Gem, TrendingUp,
  MapPin, Users, X, ExternalLink, Loader2,
} from 'lucide-react';
import { api, compact, timeAgo } from '../../lib/api';
import { ARENA } from '../../lib/classTheme';
import { ArenaTitle, ArenaTabs, ArenaEmpty } from '../../components/ArenaFrame';
import { Sheet, SkeletonList, RankBadge } from '../../components/ui';

const STATUS = {
  sent:       { label: 'Sent',       color: '#97815f' },
  registered: { label: 'Registered', color: '#3b82f6' },
  approved:   { label: 'Approved',   color: '#22c55e' },
  rewarded:   { label: 'Rewarded',   color: '#c9973a' },
  rejected:   { label: 'Rejected',   color: '#ef4444' },
  expired:    { label: 'Expired',    color: '#b8a082' },
};

export default function AdminInvites() {
  const qc = useQueryClient();
  const [tab, setTab] = useState('leads');
  const [status, setStatus] = useState('');
  const [search, setSearch] = useState('');
  const [noteOpen, setNoteOpen] = useState(null);
  const [note, setNote] = useState('');

  const { data: list, isLoading } = useQuery({
    queryKey: ['adminInvites', status, search],
    queryFn: () => api.get('/shop-invites/admin/list', {
      params: { status: status || undefined, q: search || undefined },
    }).then(r => r.data),
    enabled: tab === 'leads',
  });

  const { data: stats } = useQuery({
    queryKey: ['inviteStats'],
    queryFn: () => api.get('/shop-invites/admin/stats').then(r => r.data),
    enabled: tab === 'stats',
  });

  const contacted = useMutation({
    mutationFn: ({ id, note }) =>
      api.post(`/shop-invites/admin/${id}/contacted`, { contacted: true, note }),
    onSuccess: () => {
      toast.success('Marked as contacted');
      setNoteOpen(null); setNote('');
      qc.invalidateQueries(['adminInvites']);
    },
    onError: e => toast.error(e.message),
  });

  const setStatusM = useMutation({
    mutationFn: ({ id, status }) => api.post(`/shop-invites/admin/${id}/status`, { status }),
    onSuccess: () => { toast.success('Status updated'); qc.invalidateQueries(['adminInvites']); },
    onError: e => toast.error(e.message),
  });

  const pending = list?.filter(x => !x.contacted).length || 0;

  return (
    <div className="space-y-4">
      {/* ═══ HEADER ═══ */}
      <div className="card-arena p-4 flex items-center gap-3">
        <div className="w-11 h-11 grid place-items-center"
          style={{
            borderRadius: 'var(--radius)',
            background: 'var(--accent-soft)',
            border: `1px solid ${ARENA.gold}`,
          }}>
          <Store size={20} style={{ color: ARENA.arenaText }} />
        </div>
        <div className="flex-1 min-w-0">
          <h2 className="font-display text-lg font-extrabold neon-glow-text"
            style={{ color: ARENA.arenaText }}>
            Shop Leads
          </h2>
          <p className="text-[11px] font-semibold" style={{ color: ARENA.arenaTextSoft }}>
            Businesses invited by users · 1000 pts bounty
          </p>
        </div>
        {pending > 0 && (
          <span className="chip-gold shrink-0">{pending} NEW</span>
        )}
      </div>

      <ArenaTabs value={tab} onChange={setTab} tabs={[
        { key: 'leads', label: 'Leads', icon: Store, badge: pending },
        { key: 'stats', label: 'Stats', icon: TrendingUp },
      ]} />

      {/* ═══════════ LEADS ═══════════ */}
      {tab === 'leads' && (
        <>
          <div className="space-y-2">
            <div className="relative">
              <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2"
                style={{ color: 'var(--text-muted)' }} />
              <input className="input pl-9" placeholder="Search business, email, phone…"
                value={search} onChange={e => setSearch(e.target.value)} />
            </div>
            <div className="flex gap-1.5 overflow-x-auto no-scrollbar">
              <Chip active={!status} onClick={() => setStatus('')} label="All" />
              {Object.entries(STATUS).map(([k, v]) => (
                <Chip key={k} active={status === k} onClick={() => setStatus(k)}
                  label={v.label} color={v.color} />
              ))}
            </div>
          </div>

          {isLoading ? <SkeletonList n={5} h="h-32" />
            : !list?.length
              ? <ArenaEmpty emoji="🏪" title="No leads yet"
                  sub="Users can invite local businesses from their city page." />
              : <div className="space-y-2">
                  {list.map(inv => {
                    const s = STATUS[inv.status] || STATUS.sent;
                    return (
                      <div key={inv.id} className="card p-3"
                        style={!inv.contacted ? { borderColor: ARENA.gold, borderWidth: 2 } : {}}>
                        <div className="flex items-start gap-3">
                          <div className="w-10 h-10 grid place-items-center shrink-0"
                            style={{
                              borderRadius: 'var(--radius-sm)',
                              background: `${s.color}18`, color: s.color,
                            }}>
                            <Store size={17} />
                          </div>

                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-1.5 flex-wrap">
                              <p className="font-display font-bold text-sm truncate">
                                {inv.business_name}
                              </p>
                              <span className="chip"
                                style={{ background: `${s.color}20`, color: s.color }}>
                                {s.label}
                              </span>
                              {!inv.contacted && <span className="chip-gold !text-[8px]">NEW</span>}
                            </div>

                            <p className="text-[11px] font-semibold mt-0.5"
                              style={{ color: 'var(--text-muted)' }}>
                              by @{inv.inviter}
                              {inv.city_handle && ` · ${inv.city_handle}`}
                              {` · ${timeAgo(inv.created_at)}`}
                            </p>

                            <div className="flex flex-wrap gap-2 mt-1.5">
                              {inv.contact_name && (
                                <span className="text-[11px] font-bold flex items-center gap-1"
                                  style={{ color: 'var(--text-soft)' }}>
                                  <Users size={10} /> {inv.contact_name}
                                </span>
                              )}
                              {inv.contact_email && (
                                <a href={`mailto:${inv.contact_email}`}
                                  className="text-[11px] font-bold flex items-center gap-1 hover:underline"
                                  style={{ color: ARENA.goldD }}>
                                  <Mail size={10} /> {inv.contact_email}
                                </a>
                              )}
                              {inv.contact_phone && (
                                <a href={`tel:${inv.phone_country || ''}${inv.contact_phone}`}
                                  className="text-[11px] font-bold flex items-center gap-1 hover:underline"
                                  style={{ color: ARENA.goldD }}>
                                  <Phone size={10} /> {inv.phone_country} {inv.contact_phone}
                                </a>
                              )}
                            </div>

                            {inv.business_type && (
                              <span className="chip-ink mt-1.5">{inv.business_type}</span>
                            )}

                            {inv.note && (
                              <p className="text-[11px] mt-1.5 italic p-2"
                                style={{
                                  borderRadius: 'var(--radius-sm)',
                                  background: 'var(--surface-alt)',
                                  color: 'var(--text-soft)',
                                }}>
                                "{inv.note}"
                              </p>
                            )}

                            {inv.admin_note && (
                              <p className="text-[10px] mt-1.5 font-semibold"
                                style={{ color: 'var(--success)' }}>
                                ✓ {inv.admin_note}
                                {inv.contacted_by_name && ` — @${inv.contacted_by_name}`}
                              </p>
                            )}
                          </div>

                          {inv.status === 'rewarded' && (
                            <div className="text-right shrink-0">
                              <p className="font-display font-black text-sm" style={{ color: ARENA.goldD }}>
                                💎{inv.reward_points}
                              </p>
                              <p className="text-[9px] font-bold" style={{ color: 'var(--text-muted)' }}>
                                PAID
                              </p>
                            </div>
                          )}
                        </div>

                        <div className="flex gap-1.5 mt-2.5">
                          {!inv.contacted && (
                            <button onClick={() => { setNoteOpen(inv); setNote(''); }}
                              className="btn-primary btn-sm flex-1">
                              <Check size={12} /> Mark contacted
                            </button>
                          )}
                          {inv.status === 'sent' && (
                            <button onClick={() => setStatusM.mutate({ id: inv.id, status: 'rejected' })}
                              className="btn btn-sm"
                              style={{ background: 'rgba(239,68,68,.10)', color: ARENA.blood }}>
                              <X size={12} /> Reject
                            </button>
                          )}
                          {inv.shop_slug && (
                            <Link to={`/shop/${inv.shop_slug}`} className="btn-ghost btn-sm flex-1">
                              <ExternalLink size={12} /> View shop
                            </Link>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>}
        </>
      )}

      {/* ═══════════ STATS ═══════════ */}
      {tab === 'stats' && (
        !stats ? <SkeletonList n={4} h="h-24" /> : (
          <>
            <div className="grid grid-cols-3 gap-2">
              <Stat v={stats.totals.total} l="Total leads" />
              <Stat v={stats.totals.rewarded} l="Converted" hi />
              <Stat v={stats.totals.not_contacted} l="To call" danger={stats.totals.not_contacted > 0} />
            </div>

            <div className="grid grid-cols-3 gap-2">
              <Stat v={stats.totals.sent} l="Sent" />
              <Stat v={stats.totals.registered} l="Registered" />
              <Stat v={`💎${compact(stats.totals.points_paid)}`} l="Paid out" />
            </div>

            <div className="card p-3.5">
              <ArenaTitle title="Conversion funnel" latin="Progressus" />
              <Funnel stages={[
                { l: 'Invited', v: stats.totals.total, c: '#97815f' },
                { l: 'Registered', v: stats.totals.registered + stats.totals.rewarded, c: '#3b82f6' },
                { l: 'Rewarded', v: stats.totals.rewarded, c: ARENA.gold },
              ]} />
            </div>

            {stats.topInviters?.length > 0 && (
              <div>
                <ArenaTitle title="Top recruiters" latin="Legati" laurel />
                <div className="card p-2 space-y-1">
                  {stats.topInviters.map((u, i) => (
                    <div key={u.username} className="flex items-center gap-3 p-2.5"
                      style={{ borderRadius: 'var(--radius-sm)' }}>
                      <RankBadge rank={i + 1} size={30} />
                      <div className="flex-1 min-w-0">
                        <p className="font-display font-bold text-sm truncate">
                          {u.display_name || u.username}
                        </p>
                        <p className="text-[11px] font-semibold" style={{ color: 'var(--text-muted)' }}>
                          @{u.username}
                        </p>
                      </div>
                      <div className="text-right shrink-0">
                        <p className="font-display font-black text-sm" style={{ color: ARENA.goldD }}>
                          {u.rewarded}/{u.invites}
                        </p>
                        <p className="text-[9px] font-bold" style={{ color: 'var(--text-muted)' }}>
                          CONVERTED
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {stats.byCity?.length > 0 && (
              <div>
                <ArenaTitle title="By city" latin="Per Urbes" />
                <div className="card p-2 space-y-1">
                  {stats.byCity.map(c => (
                    <div key={c.handle} className="flex items-center gap-3 p-2.5"
                      style={{ borderRadius: 'var(--radius-sm)' }}>
                      <MapPin size={15} style={{ color: 'var(--text-muted)' }} />
                      <div className="flex-1 min-w-0">
                        <p className="font-display font-bold text-sm truncate">{c.handle}</p>
                        <p className="text-[11px]" style={{ color: 'var(--text-muted)' }}>{c.name}</p>
                      </div>
                      <span className="chip-brand">{c.rewarded}/{c.invites}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </>
        )
      )}

      {/* ═══ NOTE SHEET ═══ */}
      <Sheet open={!!noteOpen} onClose={() => setNoteOpen(null)}
        title={`Contact ${noteOpen?.business_name || ''}`}>
        {noteOpen && (
          <div className="space-y-3">
            <div className="card p-3 space-y-1.5">
              {noteOpen.contact_name && <Row l="Contact" v={noteOpen.contact_name} />}
              {noteOpen.contact_email && <Row l="Email" v={noteOpen.contact_email} />}
              {noteOpen.contact_phone && (
                <Row l="Phone" v={`${noteOpen.phone_country || ''} ${noteOpen.contact_phone}`} />
              )}
              {noteOpen.address && <Row l="Address" v={noteOpen.address} />}
              <Row l="Invited by" v={`@${noteOpen.inviter}`} />
            </div>

            <div>
              <label className="label">Call notes</label>
              <textarea className="input min-h-[90px]" value={note} maxLength={500}
                placeholder="Outcome of the call, next steps…"
                onChange={e => setNote(e.target.value)} />
            </div>

            <div className="grid grid-cols-2 gap-2">
              {noteOpen.contact_phone && (
                <a href={`tel:${noteOpen.phone_country || ''}${noteOpen.contact_phone}`}
                  className="btn-ghost">
                  <Phone size={15} /> Call now
                </a>
              )}
              <button onClick={() => contacted.mutate({ id: noteOpen.id, note })}
                disabled={contacted.isPending}
                className={`btn-primary ${noteOpen.contact_phone ? '' : 'col-span-2'}`}>
                {contacted.isPending
                  ? <Loader2 size={15} className="animate-spin" />
                  : <><Check size={15} /> Mark contacted</>}
              </button>
            </div>
          </div>
        )}
      </Sheet>
    </div>
  );
}

/* ---------------- BITS ---------------- */
function Chip({ active, onClick, label, color }) {
  return (
    <button onClick={onClick}
      className="px-3 py-1.5 text-xs font-bold whitespace-nowrap transition"
      style={{
        borderRadius: 'var(--radius-sm)',
        background: active ? (color || 'var(--text)') : 'var(--surface)',
        color: active ? '#fff' : 'var(--text-muted)',
        border: `1px solid ${active ? (color || 'var(--text)') : 'var(--border)'}`,
      }}>
      {label}
    </button>
  );
}

function Stat({ v, l, hi, danger }) {
  return (
    <div className="card p-3 text-center">
      <p className="font-display font-black text-xl"
        style={{ color: danger ? ARENA.blood : hi ? ARENA.goldD : 'var(--text)' }}>{v}</p>
      <p className="text-[9px] font-display font-bold uppercase mt-0.5"
        style={{ color: 'var(--text-muted)' }}>{l}</p>
    </div>
  );
}

function Funnel({ stages }) {
  const max = Math.max(...stages.map(s => s.v), 1);
  return (
    <div className="space-y-2">
      {stages.map((s, i) => {
        const pct = (s.v / max) * 100;
        const conv = i > 0 && stages[i - 1].v
          ? Math.round((s.v / stages[i - 1].v) * 100) : null;
        return (
          <div key={s.l}>
            <div className="flex justify-between text-[11px] font-bold mb-1">
              <span style={{ color: 'var(--text-soft)' }}>{s.l}</span>
              <span style={{ color: s.c }}>
                {s.v}{conv !== null && ` · ${conv}%`}
              </span>
            </div>
            <div className="h-6 overflow-hidden"
              style={{ borderRadius: 'var(--radius-sm)', background: 'var(--s-200)' }}>
              <div className="h-full transition-all duration-700"
                style={{ width: `${pct}%`, background: s.c, opacity: .85 }} />
            </div>
          </div>
        );
      })}
    </div>
  );
}

function Row({ l, v }) {
  return (
    <div className="flex justify-between text-sm">
      <span className="font-semibold" style={{ color: 'var(--text-muted)' }}>{l}</span>
      <span className="font-bold">{v}</span>
    </div>
  );
}